/** @jsxImportSource @emotion/react */
import React from "react";
import TextField from "../../../Utils/FormInputs/TextField";
import SelectInput from "../../../Utils/FormInputs/SelectInput";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";
import {
    FormGrid,
    ComputedBadge,
    BPInputGroup,
    BPSeparator,
    MUACIndicator,
    MUACDot,
    MUACLabel,
    WeightTrend,
    AlertsContainer,
    AlertCard,
    AlertIcon,
    AlertContent,
    AlertTitle,
    AlertDetail,
    AlertAction,
    InfoBanner,
    InfoBannerItem,
    InfoLabel,
    InfoValue,
    FormActions,
    PrimaryButton,
    SecondaryButton,
} from "./ANCClinicalAssessmentStyles";
import Form from "react-bootstrap/Form";
import Label from "../../../Utils/FormInputs/Label";

/* ─── Select options ──────────────────────────────────────── */
const PRESENTATION_OPTIONS = [
    { value: "cephalic", text: "Cephalic" },
    { value: "breech", text: "Breech" },
    { value: "transverse", text: "Transverse" },
    { value: "oblique", text: "Oblique" },
];

const POSITION_OPTIONS = [
    { value: "LOA", text: "LOA (Left Occipito-Anterior)" },
    { value: "ROA", text: "ROA (Right Occipito-Anterior)" },
    { value: "LOP", text: "LOP (Left Occipito-Posterior)" },
    { value: "ROP", text: "ROP (Right Occipito-Posterior)" },
    { value: "LOT", text: "LOT (Left Occipito-Transverse)" },
    { value: "ROT", text: "ROT (Right Occipito-Transverse)" },
];

const FIFTHS_OPTIONS = [
    { value: "5/5", text: "5/5 — Free (above brim)" },
    { value: "4/5", text: "4/5 — Slightly engaged" },
    { value: "3/5", text: "3/5 — Engaged" },
    { value: "2/5", text: "2/5 — Deeply engaged" },
    { value: "1/5", text: "1/5 — Almost fully engaged" },
    { value: "0/5", text: "0/5 — Fully engaged" },
];

const OEDEMA_OPTIONS = [
    { value: "none", text: "None" },
    { value: "mild", text: "Mild (+)" },
    { value: "moderate", text: "Moderate (++)" },
    { value: "severe", text: "Severe (+++)" },
];

const URINE_OPTIONS = [
    { value: "nil", text: "Nil" },
    { value: "trace", text: "Trace" },
    { value: "+", text: "+" },
    { value: "++", text: "++" },
    { value: "+++", text: "+++" },
];

const CONTACT_OPTIONS = [
    { value: 1, text: "Contact 1 — (≤12 weeks)" },
    { value: 2, text: "Contact 2 — (20 weeks)" },
    { value: 3, text: "Contact 3 — (26 weeks)" },
    { value: 4, text: "Contact 4 — (30 weeks)" },
    { value: 5, text: "Contact 5 — (34 weeks)" },
    { value: 6, text: "Contact 6 — (36 weeks)" },
    { value: 7, text: "Contact 7 — (38 weeks)" },
    { value: 8, text: "Contact 8 — (40 weeks)" },
];

/**
 * ANCContactForm — Flat multi-section clinical assessment form
 * Used at each ANC contact to capture vitals, examination, and notes
 */
const ANCContactForm = function(props) {
    const ancContact = props.ancContact;
    const contactData = ancContact.contactData;
    const handleChange = ancContact.handleChange;
    const handleSubmit = ancContact.handleSubmit;
    const resetForm = ancContact.resetForm;
    const submitted = ancContact.submitted;
    const saving = ancContact.saving;
    const bookingData = ancContact.bookingData;
    const gestationalAge = ancContact.gestationalAge;
    const bmi = ancContact.bmi;
    const muacStatus = ancContact.muacStatus;
    const weightTrend = ancContact.weightTrend;
    const dangerSigns = ancContact.dangerSigns;

    const sectionHeadingStyle = {
        fontSize: "0.85rem",
        fontWeight: 600,
        color: "#455a64",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginTop: "1.2rem",
        marginBottom: "0.6rem",
        paddingBottom: "0.3rem",
        borderBottom: "1px solid #e0e0e0",
    };

    return (
        <form onSubmit={handleSubmit}>
            {/* ═══ INFO BANNER — Gestational Age & Key Stats ═══ */}
            <InfoBanner>
                <InfoBannerItem>
                    <InfoLabel>Contact</InfoLabel>
                    <InfoValue size="large" color="#3871B7">
                        #{contactData.contact_number || "—"} / 8
                    </InfoValue>
                </InfoBannerItem>
                <InfoBannerItem>
                    <InfoLabel>Gestational Age</InfoLabel>
                    <InfoValue size="large">
                        {gestationalAge
                            ? gestationalAge.weeks + "w " + gestationalAge.days + "d"
                            : "—"}
                    </InfoValue>
                </InfoBannerItem>
                <InfoBannerItem>
                    <InfoLabel>Trimester</InfoLabel>
                    <InfoValue
                        color={
                            gestationalAge
                                ? gestationalAge.trimester === 1
                                    ? "#1565c0"
                                    : gestationalAge.trimester === 2
                                    ? "#2e7d32"
                                    : "#e65100"
                                : undefined
                        }
                    >
                        {gestationalAge
                            ? gestationalAge.trimester === 1
                                ? "1st Trimester"
                                : gestationalAge.trimester === 2
                                ? "2nd Trimester"
                                : "3rd Trimester"
                            : "—"}
                    </InfoValue>
                </InfoBannerItem>
                <InfoBannerItem>
                    <InfoLabel>EDD</InfoLabel>
                    <InfoValue>{bookingData?.edd || "—"}</InfoValue>
                </InfoBannerItem>
                <InfoBannerItem>
                    <InfoLabel>Gravida / Parity</InfoLabel>
                    <InfoValue>
                        G{bookingData?.gravida || "—"} P{bookingData?.parity || "—"}
                    </InfoValue>
                </InfoBannerItem>
                {bmi ? (
                    <InfoBannerItem>
                        <InfoLabel>BMI</InfoLabel>
                        <InfoValue color={bmi.color}>
                            {bmi.value}{" "}
                            <span style={{ fontSize: "0.72rem", fontWeight: 400 }}>
                                ({bmi.category})
                            </span>
                        </InfoValue>
                    </InfoBannerItem>
                ) : null}
            </InfoBanner>

            {/* ═══ DANGER SIGN ALERTS ═══ */}
            {dangerSigns.length > 0 ? (
                <AlertsContainer>
                    {dangerSigns.map(function(alert, idx) {
                        return (
                            <AlertCard key={idx} type={alert.type}>
                                <AlertIcon>{alert.icon}</AlertIcon>
                                <AlertContent>
                                    <AlertTitle type={alert.type}>{alert.title}</AlertTitle>
                                    <AlertDetail>{alert.detail}</AlertDetail>
                                    {alert.action ? (
                                        <AlertAction>→ {alert.action}</AlertAction>
                                    ) : null}
                                </AlertContent>
                            </AlertCard>
                        );
                    })}
                </AlertsContainer>
            ) : null}

            {/* ═══ CONTACT DATE & NUMBER ═══ */}
            <FormGrid columns={3}>
                <Form.Group>
                    <Label name="Contact Number" type />
                    <SelectInput
                        name="contact_number"
                        value={contactData.contact_number}
                        onChange={handleChange}
                        options={CONTACT_OPTIONS}
                        defaultOption="Select contact..."
                        submitted={submitted}
                    />
                </Form.Group>
                <Form.Group>
                    <Label name="Contact Date" type />
                    <TextField
                        type="date"
                        name="contact_date"
                        value={contactData.contact_date}
                        onChange={handleChange}
                        submitted={submitted}
                    />
                </Form.Group>
                <Form.Group>
                    <Label name="Next Appointment" />
                    <TextField
                        type="date"
                        name="next_appointment_date"
                        value={contactData.next_appointment_date}
                        onChange={handleChange}
                    />
                </Form.Group>
            </FormGrid>

            {/* ═══ VITAL SIGNS ═══ */}
            <div style={sectionHeadingStyle}>Vital Signs</div>
            <FormGrid columns={4}>
                {/* Weight */}
                <Form.Group>
                    <Label name="Weight (kg)" type />
                    <TextField
                        type="number"
                        name="weight_kg"
                        value={contactData.weight_kg}
                        onChange={handleChange}
                        placeholder="e.g. 65"
                        submitted={submitted}
                        step="0.1"
                        min="30"
                        max="200"
                    />
                    {bmi ? (
                        <ComputedBadge color={bmi.color}>
                            BMI: {bmi.value} — {bmi.category}
                        </ComputedBadge>
                    ) : null}
                    {weightTrend ? (
                        <WeightTrend color={weightTrend.color}>
                            {weightTrend.direction === "up" ? "↑" : weightTrend.direction === "down" ? "↓" : "→"}
                            {" "}{weightTrend.label} since last visit
                        </WeightTrend>
                    ) : null}
                </Form.Group>

                {/* MUAC */}
                <Form.Group>
                    <Label name="MUAC (cm)" />
                    <TextField
                        type="number"
                        name="muac_cm"
                        value={contactData.muac_cm}
                        onChange={handleChange}
                        placeholder="e.g. 25"
                        step="0.1"
                        min="10"
                        max="50"
                    />
                    {muacStatus ? (
                        <MUACIndicator
                            muacColor={muacStatus.color}
                            muacBg={muacStatus.bg}
                        >
                            <MUACDot color={muacStatus.color} />
                            <MUACLabel color={muacStatus.color}>
                                {muacStatus.label} — {contactData.muac_cm} cm
                            </MUACLabel>
                        </MUACIndicator>
                    ) : null}
                </Form.Group>

                {/* Blood Pressure */}
                <Form.Group>
                    <Label name="Blood Pressure (mmHg)" type />
                    <BPInputGroup>
                        <TextField
                            type="number"
                            name="blood_pressure_systolic"
                            value={contactData.blood_pressure_systolic}
                            onChange={handleChange}
                            placeholder="SYS"
                            submitted={submitted}
                            min="60"
                            max="260"
                            style={{ width: "80px", textAlign: "center" }}
                        />
                        <BPSeparator>/</BPSeparator>
                        <TextField
                            type="number"
                            name="blood_pressure_diastolic"
                            value={contactData.blood_pressure_diastolic}
                            onChange={handleChange}
                            placeholder="DIA"
                            submitted={submitted}
                            min="30"
                            max="180"
                            style={{ width: "80px", textAlign: "center" }}
                        />
                    </BPInputGroup>
                    {(parseFloat(contactData.blood_pressure_systolic) >= 140 ||
                        parseFloat(contactData.blood_pressure_diastolic) >= 90) ? (
                        <ComputedBadge color="#c53030" bg="#fed7d7">
                            ⚠️ Elevated
                        </ComputedBadge>
                    ) : null}
                </Form.Group>

                {/* Pulse Rate */}
                <Form.Group>
                    <Label name="Pulse Rate (bpm)" />
                    <TextField
                        type="number"
                        name="pulse_rate"
                        value={contactData.pulse_rate}
                        onChange={handleChange}
                        placeholder="e.g. 78"
                        min="40"
                        max="200"
                    />
                </Form.Group>

                {/* Temperature */}
                <Form.Group>
                    <Label name="Temperature (°C)" />
                    <TextField
                        type="number"
                        name="temperature"
                        value={contactData.temperature}
                        onChange={handleChange}
                        placeholder="e.g. 36.5"
                        step="0.1"
                        min="34"
                        max="42"
                    />
                    {parseFloat(contactData.temperature) >= 37.5 ? (
                        <ComputedBadge color="#c53030" bg="#fed7d7">
                            🌡️ Fever
                        </ComputedBadge>
                    ) : null}
                </Form.Group>

                {/* Respiratory Rate */}
                <Form.Group>
                    <Label name="Respiratory Rate (/min)" />
                    <TextField
                        type="number"
                        name="respiratory_rate"
                        value={contactData.respiratory_rate}
                        onChange={handleChange}
                        placeholder="e.g. 18"
                        min="8"
                        max="60"
                    />
                </Form.Group>
            </FormGrid>

            {/* ═══ ABDOMINAL EXAMINATION ═══ */}
            <div style={sectionHeadingStyle}>Abdominal Examination</div>
            <FormGrid columns={4}>
                {/* Fundal Height */}
                <Form.Group>
                    <Label name="Fundal Height (cm)" />
                    <TextField
                        type="number"
                        name="fundal_height_cm"
                        value={contactData.fundal_height_cm}
                        onChange={handleChange}
                        placeholder="e.g. 28"
                        min="8"
                        max="45"
                    />
                    {contactData.fundal_height_cm && gestationalAge ? (
                        <ComputedBadge
                            color={
                                Math.abs(
                                    parseFloat(contactData.fundal_height_cm) -
                                        gestationalAge.weeks
                                ) > 3
                                    ? "#c53030"
                                    : "#2e7d32"
                            }
                        >
                            {Math.abs(
                                parseFloat(contactData.fundal_height_cm) -
                                    gestationalAge.weeks
                            ) > 3
                                ? "⚠ Size-date discrepancy"
                                : "✓ Corresponds to dates"}
                        </ComputedBadge>
                    ) : null}
                </Form.Group>

                {/* Fetal Heart Rate */}
                <Form.Group>
                    <Label name="Fetal Heart Rate (bpm)" />
                    <TextField
                        type="number"
                        name="fetal_heart_rate"
                        value={contactData.fetal_heart_rate}
                        onChange={handleChange}
                        placeholder="e.g. 140"
                        min="60"
                        max="220"
                    />
                    {contactData.fetal_heart_rate ? (
                        <ComputedBadge
                            color={
                                parseFloat(contactData.fetal_heart_rate) < 110 ||
                                parseFloat(contactData.fetal_heart_rate) > 160
                                    ? "#c53030"
                                    : "#2e7d32"
                            }
                            bg={
                                parseFloat(contactData.fetal_heart_rate) < 110 ||
                                parseFloat(contactData.fetal_heart_rate) > 160
                                    ? "#fed7d7"
                                    : "#e8f5e9"
                            }
                        >
                            {parseFloat(contactData.fetal_heart_rate) < 110
                                ? "💗 Bradycardia"
                                : parseFloat(contactData.fetal_heart_rate) > 160
                                ? "💗 Tachycardia"
                                : "✓ Normal"}
                        </ComputedBadge>
                    ) : null}
                </Form.Group>

                {/* Fetal Presentation */}
                <Form.Group>
                    <Label name="Presentation" />
                    <SelectInput
                        name="fetal_presentation"
                        value={contactData.fetal_presentation}
                        onChange={handleChange}
                        options={PRESENTATION_OPTIONS}
                        defaultOption="Select..."
                    />
                    {contactData.fetal_presentation === "breech" && gestationalAge && gestationalAge.weeks >= 36 ? (
                        <ComputedBadge color="#c53030" bg="#fed7d7">
                            ⚠ Breech at ≥36 weeks — consider ECV
                        </ComputedBadge>
                    ) : null}
                </Form.Group>

                {/* Fetal Position */}
                <Form.Group>
                    <Label name="Fetal Position" />
                    <SelectInput
                        name="fetal_position"
                        value={contactData.fetal_position}
                        onChange={handleChange}
                        options={POSITION_OPTIONS}
                        defaultOption="Select..."
                    />
                </Form.Group>

                {/* Presenting Part to Brim */}
                <Form.Group>
                    <Label name="Presenting Part (Fifths)" />
                    <SelectInput
                        name="presenting_part_to_brim"
                        value={contactData.presenting_part_to_brim}
                        onChange={handleChange}
                        options={FIFTHS_OPTIONS}
                        defaultOption="Select..."
                    />
                </Form.Group>

                {/* Oedema */}
                <Form.Group>
                    <Label name="Oedema" />
                    <SelectInput
                        name="oedema"
                        value={contactData.oedema}
                        onChange={handleChange}
                        options={OEDEMA_OPTIONS}
                        defaultOption="Select..."
                    />
                    {contactData.oedema === "severe" ? (
                        <ComputedBadge color="#c53030" bg="#fed7d7">
                            ⚠️ Severe — evaluate for pre-eclampsia
                        </ComputedBadge>
                    ) : null}
                </Form.Group>
            </FormGrid>

            {/* ═══ URINALYSIS & HAEMOGLOBIN ═══ */}
            <div style={sectionHeadingStyle}>Urinalysis &amp; Haemoglobin</div>
            <FormGrid columns={3}>
                {/* Urine Protein */}
                <Form.Group>
                    <Label name="Urine Protein" />
                    <SelectInput
                        name="urine_protein"
                        value={contactData.urine_protein}
                        onChange={handleChange}
                        options={URINE_OPTIONS}
                        defaultOption="Select..."
                    />
                    {(contactData.urine_protein === "++" ||
                        contactData.urine_protein === "+++") ? (
                        <ComputedBadge color="#c53030" bg="#fed7d7">
                            ⚠️ Significant proteinuria
                        </ComputedBadge>
                    ) : null}
                </Form.Group>

                {/* Urine Glucose */}
                <Form.Group>
                    <Label name="Urine Glucose" />
                    <SelectInput
                        name="urine_glucose"
                        value={contactData.urine_glucose}
                        onChange={handleChange}
                        options={URINE_OPTIONS}
                        defaultOption="Select..."
                    />
                    {(contactData.urine_glucose === "++" ||
                        contactData.urine_glucose === "+++") ? (
                        <ComputedBadge color="#b7791f" bg="#fefcbf">
                            ⚠️ Screen for gestational diabetes
                        </ComputedBadge>
                    ) : null}
                </Form.Group>

                {/* Haemoglobin */}
                <Form.Group>
                    <Label name="Haemoglobin (g/dL)" />
                    <TextField
                        type="number"
                        name="haemoglobin"
                        value={contactData.haemoglobin}
                        onChange={handleChange}
                        placeholder="e.g. 12.5"
                        step="0.1"
                        min="3"
                        max="20"
                    />
                    {contactData.haemoglobin ? (
                        <ComputedBadge
                            color={
                                parseFloat(contactData.haemoglobin) < 7
                                    ? "#c53030"
                                    : parseFloat(contactData.haemoglobin) < 11
                                    ? "#b7791f"
                                    : "#2e7d32"
                            }
                            bg={
                                parseFloat(contactData.haemoglobin) < 7
                                    ? "#fed7d7"
                                    : parseFloat(contactData.haemoglobin) < 11
                                    ? "#fefcbf"
                                    : "#e8f5e9"
                            }
                        >
                            {parseFloat(contactData.haemoglobin) < 7
                                ? "🚨 Severe Anaemia"
                                : parseFloat(contactData.haemoglobin) < 10
                                ? "⚠️ Moderate Anaemia"
                                : parseFloat(contactData.haemoglobin) < 11
                                ? "⚠️ Mild Anaemia"
                                : "✓ Normal"}
                        </ComputedBadge>
                    ) : null}
                </Form.Group>
            </FormGrid>

            {/* ═══ CLINICAL NOTES ═══ */}
            <div style={sectionHeadingStyle}>Clinical Notes</div>
            <FormGrid columns={1}>
                {/* Complaints */}
                <Form.Group>
                    <Label name="Presenting Complaints" />
                    <ResizableTextarea
                        rows={2}
                        name="complaints"
                        value={contactData.complaints}
                        onChange={handleChange}
                        placeholder="Document the patient's presenting complaints for this visit..."
                    />
                </Form.Group>

                {/* Clinical Notes */}
                <Form.Group>
                    <Label name="Clinical Notes & Plan" />
                    <ResizableTextarea
                        rows={3}
                        name="clinical_notes"
                        value={contactData.clinical_notes}
                        onChange={handleChange}
                        placeholder="Document clinical findings, assessments, and management plan..."
                    />
                </Form.Group>
            </FormGrid>

            {/* ═══ FORM ACTIONS ═══ */}
            <FormActions>
                <SecondaryButton type="button" onClick={resetForm}>
                Clear Form
                </SecondaryButton>
                <button type="submit" disabled={saving} className="sms-btn sms-info-btn btn">
                    {saving ? (
                        <span>
                            <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                                style={{ marginRight: "6px" }}
                            />
                            Saving...
                        </span>
                    ) : (
                        <span>✓ Save ANC Contact</span>
                    )}
                </button>
            </FormActions>
        </form>
    );
};

export default ANCContactForm;
